import { Stack, Typography } from "@mui/material";
import { EditableHotkey } from "components/identifyEventsTool/guide/EditableHotkey";
import { Hotkey } from "components/identifyEventsTool/guide/Hotkey";
import { useTenantTranslation } from "hooks/formatters";
import { getModifierKey } from "hooks/usePlatform";

export type HotkeyLabelOverrides = Readonly<{
  confirm?: string;
  escape?: string;
  skip?: string;
}>;

type EditEventHotkeysProps = Readonly<{
  showAreaAndMaterial: boolean;
  showDelete: boolean;
  showNavigation: boolean;
  hotkeyLabels?: HotkeyLabelOverrides;
}>;

export const EditEventHotkeys = ({
  showAreaAndMaterial,
  showDelete,
  showNavigation,
  hotkeyLabels,
}: EditEventHotkeysProps) => {
  const { t } = useTenantTranslation();
  const { ctrl, shift } = getModifierKey();

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {t("hotkeys")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        fontStyle="italic"
        sx={{ mb: 0.5 }}
      >
        {t("clickAnyKeyToCustomize")}
      </Typography>
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Stack direction="row" spacing={0.5}>
            <Hotkey label={ctrl} />
            <Hotkey label="↵" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {hotkeyLabels?.confirm ?? t("saveAndClose")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Hotkey label="Esc" />
          <Typography variant="body2" color="text.secondary">
            {hotkeyLabels?.escape ?? t("saveAndClose")}
          </Typography>
        </Stack>
        {hotkeyLabels?.skip != null ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Hotkey label="Tab" />
            <Typography variant="body2" color="text.secondary">
              {hotkeyLabels.skip}
            </Typography>
          </Stack>
        ) : null}
        {showAreaAndMaterial ? (
          <>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EditableHotkey hotkeyId="changeAreaMode" />
              <Typography variant="body2" color="text.secondary">
                {t("changeArea")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EditableHotkey hotkeyId="changeMaterialMode" />
              <Typography variant="body2" color="text.secondary">
                {t("changeMaterial")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EditableHotkey hotkeyId="changeCoMaterialMode" />
              <Typography variant="body2" color="text.secondary">
                {t("changeCoMaterial")}
              </Typography>
            </Stack>
          </>
        ) : null}
        {showDelete ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <EditableHotkey hotkeyId="deleteEvent" />
            <Typography variant="body2" color="text.secondary">
              {t("deleteEvent")}
            </Typography>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <EditableHotkey hotkeyId="toggleResolved" />
          <Typography variant="body2" color="text.secondary">
            {t("markAsResolved")}
          </Typography>
        </Stack>
        {showNavigation ? (
          <>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EditableHotkey hotkeyId="nextItem" />
              <Typography variant="body2" color="text.secondary">
                {t("saveAndNextEvent")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EditableHotkey hotkeyId="previousItem" />
              <Typography variant="body2" color="text.secondary">
                {t("saveAndPreviousEvent")}
              </Typography>
            </Stack>
          </>
        ) : null}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Stack direction="row" spacing={0.5}>
            <Hotkey label={ctrl} />
            <Hotkey label="Z" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("undo")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Stack direction="row" spacing={0.5}>
            <Hotkey label={ctrl} />
            <Hotkey label={shift} />
            <Hotkey label="Z" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("redo")}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
